'use client'

import { useRef, useEffect, useCallback, useState } from 'react'
import { createPortal } from 'react-dom'
import { useSession } from 'next-auth/react'
import { useTranslations } from 'use-intl'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '@/lib/utils'
import { apiClient } from '@/lib/api-client'
import { useAI } from '@/context/AIContext'
import { workbenchApi } from '@/lib/ap/workbench'
import { useApLabels } from '@/hooks/useApLabels'
import type { AppMessages } from '@/i18n/config'
import { WORKBENCH_TABS } from '@/lib/ap/workbench-tabs'
import { contextChipParts, humanize, pageKind } from '@/lib/ai/page-context'
import { INVOICE_UPDATED_EVENT } from '@/lib/ai/types'
import type { ActionAttachmentData, ChatRequest, ChatResponse, InvoiceUpdatedDetail } from '@/lib/ai/types'
import { Icons } from '@/components/ui/icons'
import { Button } from '@/components/ui/button'
import { ChatMessage } from './ChatMessage'
import { ChatInput } from './ChatInput'
import { CapabilityBubbles } from './CapabilityBubbles'

type DataTabKey = keyof AppMessages['aiPanel']['context']['dataManagerTabs']

// ============================================================================
// Animation Variants
// ============================================================================

const overlayVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 },
}

const modalVariants = {
  hidden: { 
    opacity: 0, 
    scale: 0.95,
    y: 20,
  },
  visible: { 
    opacity: 1, 
    scale: 1,
    y: 0,
    transition: {
      type: 'spring',
      damping: 25,
      stiffness: 300,
    }
  },
  exit: { 
    opacity: 0, 
    scale: 0.95,
    y: 20,
    transition: {
      duration: 0.15,
    }
  },
}

// ============================================================================
// AI Manager Component
// ============================================================================

const BRAND_NAME = 'Supervity'

export function AIManager() {
  const { data: session } = useSession()
  const {
    aiEnabled,
    isManagerOpen,
    closeManager,
    chatHistory,
    addMessage,
    clearHistory,
    isTyping,
    setIsTyping,
    chatContext,
  } = useAI()

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const modalRef = useRef<HTMLDivElement>(null)
  const isWorkbench = pageKind(chatContext.page) === 'workbench'

  // Human invoice label (INV00789) for the context chip — the URL only carries the db id.
  const [invoiceLabel, setInvoiceLabel] = useState<string | null>(null)
  useEffect(() => {
    const id = chatContext.invoice_id
    if (!isManagerOpen || id == null) {
      setInvoiceLabel(null)
      return
    }
    let cancelled = false
    workbenchApi
      .detail(id)
      .then((inv) => { if (!cancelled) setInvoiceLabel(inv.invoice_id) })
      .catch(() => { if (!cancelled) setInvoiceLabel(null) })
    return () => { cancelled = true }
  }, [isManagerOpen, chatContext.invoice_id])

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [chatHistory, isTyping])

  // Handle escape key to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isManagerOpen) {
        closeManager()
      }
    }
    
    if (isManagerOpen) {
      document.addEventListener('keydown', handleKeyDown)
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden'
    }
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = ''
    }
  }, [isManagerOpen, closeManager])

  // Focus trap - focus the modal when it opens
  useEffect(() => {
    if (isManagerOpen && modalRef.current) {
      modalRef.current.focus()
    }
  }, [isManagerOpen])

  /** Tell the page an invoice changed (useWorkbench listens and re-fetches). */
  const notifyInvoiceUpdated = useCallback((invoiceId: number | null) => {
    if (typeof window === 'undefined') return
    const detail: InvoiceUpdatedDetail = { invoiceId }
    window.dispatchEvent(new CustomEvent<InvoiceUpdatedDetail>(INVOICE_UPDATED_EVENT, { detail }))
  }, [])

  // Handle sending a message
  const handleSendMessage = useCallback(async (content: string) => {
    // Add user message
    addMessage({ role: 'user', content })

    // Add loading message
    addMessage({ role: 'assistant', content: '', isLoading: true })
    setIsTyping(true)

    try {
      const payload: ChatRequest = {
        message: content,
        history: chatHistory.filter(m => !m.isLoading).map(m => ({
          role: m.role,
          content: m.content,
        })),
        // C3 — page, invoice_id, queue, tab, highlight, selection
        context: chatContext,
      }
      const data = await apiClient.post<ChatResponse>('/api/ai/chat', payload)

      const attachments = Array.isArray(data.attachments) ? data.attachments : []
      const suggestedPrompts = Array.isArray(data.suggested_prompts) && data.suggested_prompts.length
        ? data.suggested_prompts
        : Array.isArray(data.suggestions) ? data.suggestions : []

      // Remove loading message and add real response
      addMessage({
        role: 'assistant',
        content: data.response || (attachments.length ? '' : 'I apologize, but I encountered an issue processing your request.'),
        toolCalls: data.tool_calls ?? undefined,
        attachments,
        suggestedPrompts,
      })

      // Actions with side effects → let the current page refresh its data.
      const actions = attachments.filter(a => a.type === 'action')
      if (actions.length) {
        const touched = new Set<number | null>()
        for (const a of actions) {
          const d = (a.data ?? {}) as ActionAttachmentData
          const ids = Array.isArray(d.results) && d.results.length
            ? d.results.map(r => (typeof r.invoice_id === 'number' ? r.invoice_id : null))
            : [typeof d.invoice_id === 'number' ? d.invoice_id : chatContext.invoice_id]
          ids.forEach(id => touched.add(id))
        }
        touched.forEach(id => notifyInvoiceUpdated(id))
      }
    } catch {
      addMessage({
        role: 'assistant',
        content: 'I apologize, but I encountered an error. Please try again.',
      })
    } finally {
      setIsTyping(false)
    }
  }, [addMessage, chatHistory, chatContext, setIsTyping, notifyInvoiceUpdated])

  // Handle quick action click
  const handleQuickAction = (action: string) => {
    handleSendMessage(action)
  }

  // Manual refresh from an action card: workbench re-fetches on the event; other pages reload.
  const handleRefresh = useCallback(() => {
    if (isWorkbench) {
      notifyInvoiceUpdated(chatContext.invoice_id)
    } else if (typeof window !== 'undefined') {
      window.location.reload()
    }
  }, [isWorkbench, notifyInvoiceUpdated, chatContext.invoice_id])

  const tPanel = useTranslations('aiPanel')
  const tManager = useTranslations('aiPanel.manager')
  const tWorkbenchTabs = useTranslations('workbench.page.tabs')
  const tDataTabs = useTranslations('aiPanel.context.dataManagerTabs')
  const apLabels = useApLabels()
  const chipParts = contextChipParts(
    chatContext,
    {
      page: (kind) => tPanel(`pages.${kind}`),
      explorerTab: (tab) => tPanel('context.explorerTab', { tab: apLabels.explorerTab(tab, humanize(tab)) }),
      selected: (count) => tPanel('context.selected', { count, n: String(count) }),
      workbenchTab: (aiLabel) => {
        const found = WORKBENCH_TABS.find((tab) => tab.aiLabel === aiLabel)
        return found ? tWorkbenchTabs(found.key) : aiLabel
      },
      dataManagerTab: (code) => (tDataTabs.has(code as DataTabKey) ? tDataTabs(code as DataTabKey) : humanize(code)),
    },
    invoiceLabel,
  )

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      closeManager()
    }
  }

  // Only render in browser (for portal); never for read-only roles.
  if (typeof window === 'undefined' || !aiEnabled) return null

  return createPortal(
    <AnimatePresence mode="wait">
      {isManagerOpen && (
        <>
          {/* Backdrop with blur - covers entire viewport including sidebar */}
          <motion.div
            key="ai-manager-backdrop"
            variants={overlayVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            transition={{ duration: 0.2 }}
            onClick={handleBackdropClick}
            className="fixed inset-0 z-modal-backdrop flex items-center justify-center p-4 sm:p-8 md:p-12"
            style={{
              backgroundColor: 'rgba(26, 35, 64, 0.5)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
            }}
            aria-hidden="true"
          >
            {/* Modal Container - centered and properly sized */}
            <motion.div
              ref={modalRef}
              key="ai-manager-modal"
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              role="dialog"
              aria-modal="true"
              aria-labelledby="ai-manager-title"
              aria-describedby="ai-manager-description"
              tabIndex={-1}
              onClick={(e) => e.stopPropagation()}
              className={cn(
                // Z-index for proper stacking
                'z-modal',
                // Size and positioning
                'relative w-full max-w-2xl',
                'h-[80vh] max-h-[700px] min-h-[450px]',
                // Solid white background for better readability
                'bg-white',
                // Border and shadow
                'border border-gray-200',
                'rounded-2xl',
                'shadow-2xl',
                // Layout
                'flex flex-col',
                'overflow-hidden'
              )}
            >
              {/* Decorative gradient border */}
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-brand-cornflower/20 via-transparent to-brand-purple/20 pointer-events-none" />
              
              {/* Header */}
              <div className="relative border-b border-border/30 px-6 py-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    {/* AI Icon */}
                    <div className={cn(
                      'flex h-12 w-12 items-center justify-center rounded-xl',
                      'bg-gradient-to-br from-brand-navy via-brand-purple to-brand-cornflower',
                      'shadow-lg shadow-brand-purple/25',
                      'ring-2 ring-white/50'
                    )}>
                      <Icons.sparkles className="h-6 w-6 text-white" strokeWidth={1.5} />
                    </div>
                    
                    <div>
                      <h2 
                        id="ai-manager-title"
                        className="text-xl font-semibold text-brand-navy"
                      >
                        {tManager('title', { brand: BRAND_NAME })}
                      </h2>
                      <p 
                        id="ai-manager-description"
                        className="text-sm text-muted-foreground"
                      >
                        {tManager('subtitle')}
                      </p>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    {chatHistory.length > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={clearHistory}
                        className="text-muted-foreground hover:text-foreground gap-1.5"
                        title={tManager('clearConversation')}
                      >
                        <Icons.trash className="h-4 w-4" strokeWidth={1.5} />
                        <span className="hidden sm:inline">{tManager('clear')}</span>
                      </Button>
                    )}
                    
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={closeManager}
                      className="text-muted-foreground hover:text-foreground hover:bg-red-50 hover:text-red-600"
                      title={tManager('closeEsc')}
                    >
                      <Icons.close className="h-5 w-5" strokeWidth={1.5} />
                    </Button>
                  </div>
                </div>

                {/* Page Context Badge — exactly what the copilot knows (C3) */}
                <div className="mt-4 flex items-center gap-3">
                  <div
                    className={cn(
                      'inline-flex min-w-0 items-center gap-2 rounded-full px-3 py-1.5',
                      'bg-gradient-to-r from-brand-cornflower/10 to-brand-purple/10',
                      'text-brand-navy border border-brand-cornflower/20',
                      'text-sm font-medium'
                    )}
                    title={tManager('contextTooltip', { context: JSON.stringify(chatContext) })}
                  >
                    <Icons.globe className="h-4 w-4 flex-shrink-0 text-brand-cornflower" strokeWidth={1.5} />
                    <span className="truncate">
                      <span className="text-muted-foreground">{tManager('contextLabel')}</span>{' '}
                      {chipParts.map((part, i) => (
                        <span key={i}>
                          {i > 0 && <span className="mx-1 text-muted-foreground/60">·</span>}
                          {part}
                        </span>
                      ))}
                    </span>
                  </div>
                  
                  <kbd className={cn(
                    'hidden items-center rounded-md px-2 py-1 sm:inline-flex',
                    'text-xs font-medium',
                    'border border-border/50 bg-muted/50 text-muted-foreground'
                  )}>
                    <Icons.command className="h-3 w-3 mr-1" />
                    {tManager('toggleHint')}
                  </kbd>
                </div>
              </div>

              {/* Messages Area */}
              <div className="relative flex-1 overflow-y-auto px-6 py-5">
                {chatHistory.length === 0 ? (
                  /* Empty State */
                  <div className="flex h-full flex-col items-center justify-center text-center py-8">
                    <div className={cn(
                      'mb-6 flex h-20 w-20 items-center justify-center rounded-2xl',
                      'bg-gradient-to-br from-brand-cornflower/20 to-brand-purple/20',
                      'ring-1 ring-brand-cornflower/30'
                    )}>
                      <Icons.messageSquare className="h-10 w-10 text-brand-cornflower" strokeWidth={1.5} />
                    </div>
                    
                    <h3 className="font-display text-xl font-semibold text-brand-navy">
                      {tManager('emptyTitle')}
                    </h3>
                    <p className="mt-2 max-w-sm text-muted-foreground">
                      {tManager('emptyBody')}
                    </p>

                    {/* Quick Actions - Capability Bubbles */}
                    <div className="mt-8 w-full max-w-lg">
                      <CapabilityBubbles onSelect={handleQuickAction} context={chatContext} />
                    </div>
                  </div>
                ) : (
                  /* Chat Messages */
                  <div className="space-y-4">
                    {chatHistory.map((message, i) => (
                      <ChatMessage
                        key={message.id}
                        message={message}
                        userName={session?.user?.name || undefined}
                        userImage={session?.user?.image}
                        isLast={i === chatHistory.length - 1}
                        onSuggestedPrompt={handleSendMessage}
                        onNavigate={closeManager}
                        onRefresh={handleRefresh}
                        disabled={isTyping}
                      />
                    ))}
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </div>

              {/* Input Area */}
              <div className="relative border-t border-border/30">
                <ChatInput
                  onSend={handleSendMessage}
                  disabled={isTyping}
                  placeholder={isTyping ? tManager('thinking') : tManager('askAnything')}
                />
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  )
}
